import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { GoogleMap, useJsApiLoader, InfoWindowF, CircleF } from '@react-google-maps/api';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { GeoLocation, PractitionerDirectory } from '../../shared/types';
import {
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import Button from '../Button';
import Loading from '../Loading';

type PractitionerMapProps = {
  practitioners: PractitionerDirectory[];
  onPractitionerSelect: (practitioner: PractitionerDirectory) => void;
  zipcode: string;
  searchRadiusMiles: number;
  onZipcodeLocationChange?: (location: GeoLocation) => void;
  focusedPractitioner?: PractitionerDirectory | null;
  setFocusedPractitioner?: (practitioner: PractitionerDirectory | null) => void;
  googleMapsApiKey: string;
};

type GeocodedPractitioner = PractitionerDirectory & {
  lat: number;
  lng: number;
};

const libraries: ('places' | 'marker')[] = ['places', 'marker'];

const MAX_ZOOM = 18;

const mapOptions: google.maps.MapOptions = {
  mapId: "practitioner-directory-google-map",
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  maxZoom: MAX_ZOOM,
};

const milesToMeters = (miles: number) => miles * 1609.34;

const PractitionerMap = ({
  practitioners,
  onPractitionerSelect,
  zipcode,
  searchRadiusMiles,
  onZipcodeLocationChange,
  focusedPractitioner,
  setFocusedPractitioner,
  googleMapsApiKey
}: PractitionerMapProps) => {
  const [ geocodedPractitioners, setGeocodedPractitioners ] = useState<GeocodedPractitioner[]>([]);
  const [ selectedPractitioners, setSelectedPractitioners ] = useState<GeocodedPractitioner[] | null>(null);
  const [ clusterPractitioners, setClusterPractitioners ] = useState<GeocodedPractitioner[] | null>(null);
  const [ isGeocoding, setIsGeocoding ] = useState(false);
  const [ zipcodeCenter, setZipcodeCenter ] = useState<GeoLocation | null>(null);

  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [ _, setMarkerClusterer ] = useState<MarkerClusterer | null>(null);

  const createMarkerContent = useCallback((count: number, isCluster: boolean): HTMLElement => {
    const size = isCluster ? Math.min(40 + count * 2, 60) : 24;
    const container = document.createElement('div');

    createRoot(container).render(
      <div className="absolute -translate-x-1/2 -translate-y-1/2">
        <div
          className="
            rounded-full
            bg-brand-600
            border-white
            shadow-[0_2px_6px_rgba(0,0,0,0.3)]
            cursor-pointer
            flex items-center justify-center
            text-white font-bold text-xs
            border-solid
          "
          style={{ width: size, height: size, borderWidth: isCluster ? 3 : 2 }}
        >
          {count}
        </div>
      </div>
    );

    return container;
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: googleMapsApiKey,
    libraries,
  });

  useEffect(() => {
    if (focusedPractitioner && mapRef.current && geocodedPractitioners.length > 0) {
      const practitioner = geocodedPractitioners.find(
        p => p.practitioner_role_id === focusedPractitioner.practitioner_role_id
      );

        if (practitioner) {
        mapRef.current.setCenter({ lat: practitioner.lat, lng: practitioner.lng });
        mapRef.current.setZoom(12);
        setSelectedPractitioners([practitioner]);
        console.log(geocodedPractitioners.map(p => p.lat + ', ' + p.lng));
        setClusterPractitioners(geocodedPractitioners.filter(p => Math.abs(p.lat - practitioner.lat) < 0.00001 && Math.abs(p.lng - practitioner.lng) < 0.00001));
      }
    }
  }, [focusedPractitioner, geocodedPractitioners]);

  const geocodeAddress = useCallback(async (address: string): Promise<GeoLocation | null> => {
    if (!geocoderRef.current) return null;

    return new Promise((resolve) => {
      geocoderRef.current!.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          resolve(null);
        }
      });
    });
  }, []);

  const resetZoomAndCenter = useCallback((coords: GeoLocation) => {
    if (mapRef.current) {
      mapRef.current.setCenter(coords);
      const zoomLevel = searchRadiusMiles <= 5 ? 12 :
                        searchRadiusMiles <= 10 ? 11 :
                        searchRadiusMiles <= 25 ? 10 :
                        searchRadiusMiles <= 50 ? 9 : 8;
      mapRef.current.setZoom(zoomLevel);
    }
  }, [ searchRadiusMiles ]);

  const calculateCircleCenter = useCallback(async () => {
    if (!zipcode) return;

    const coords = await geocodeAddress(zipcode);
    if (coords) {
      setZipcodeCenter(coords);
      onZipcodeLocationChange?.(coords);
      resetZoomAndCenter(coords);
    }
  }, [ geocodeAddress, onZipcodeLocationChange, resetZoomAndCenter, zipcode ]);

  useEffect(() => {
    calculateCircleCenter();
  }, [ zipcode, calculateCircleCenter ]);

  // Geocode all practitioners when map loads
  useEffect(() => {
    setGeocodedPractitioners([]);
    if (!isLoaded || !geocoderRef.current || practitioners.length === 0) return;

    const geocodePractitioners = async () => {
      setIsGeocoding(true);
      const geocoded: GeocodedPractitioner[] = [];

      for (let i = 0; i < practitioners.length; i++) {
        const practitioner = practitioners[i];
        let lat = practitioner.lat;
        let lng = practitioner.lng;
        let didGeocode = false;

        if (!lat || !lng) {
          const coords = await geocodeAddress(practitioner.location);
          if (coords) {
            lat = coords.lat;
            lng = coords.lng;
            didGeocode = true;
          }
        }

        if (lat && lng) {
          geocoded.push({
            ...practitioner,
            lat,
            lng,
          });
        }

        if (didGeocode && i < practitioners.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      setGeocodedPractitioners(geocoded);
      setIsGeocoding(false);
    };

    geocodePractitioners();
  }, [ isLoaded, practitioners, geocodeAddress ]);

  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;

    const newMarkers = geocodedPractitioners.map((practitioner) => {
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: practitioner.lat, lng: practitioner.lng },
        title: practitioner.name,
      });
      marker.appendChild(createMarkerContent(1, false));

      (marker as any).practitionerData = practitioner;

      marker.addListener('click', () => {
        setSelectedPractitioners([ practitioner ]);
      });

      return marker;
    });

    const clusterer = new MarkerClusterer({
      map: mapRef.current,
      markers: newMarkers,
      algorithm: new SuperClusterAlgorithm({ maxZoom: MAX_ZOOM, radius: 80 }),
      onClusterClick: (_, cluster, map) => {
        const markers = cluster.markers;
        if (!markers || markers.length === 0) return;

        const firstMarker = markers[0] as google.maps.marker.AdvancedMarkerElement;
        const firstPos = firstMarker.position;
        if (!firstPos) return;

        const firstLat = typeof firstPos.lat === 'function' ? firstPos.lat() : firstPos.lat;
        const firstLng = typeof firstPos.lng === 'function' ? firstPos.lng() : firstPos.lng;

        // Check if all markers are at the same location (approx)
        const allSameLocation = markers.every((marker) => {
          const pos = (marker as google.maps.marker.AdvancedMarkerElement).position;
          if (!pos) return false;
          const lat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
          const lng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
          return Math.abs(lat - firstLat) < 0.00001 && Math.abs(lng - firstLng) < 0.00001;
        });

        const currentZoom = map.getZoom();
        const isHighZoom = currentZoom && currentZoom >= MAX_ZOOM;

        if (allSameLocation || isHighZoom) {
          const practitioners = markers
            .map((marker) => (marker as any).practitionerData as GeocodedPractitioner)
            .filter(Boolean);
          if (practitioners.length > 0) {
            setSelectedPractitioners(practitioners);
          }
        } else {
          const bounds = new google.maps.LatLngBounds();
          markers.forEach((marker) => {
            const pos = (marker as google.maps.marker.AdvancedMarkerElement).position;
            if (pos) bounds.extend(pos);
          });
          map.fitBounds(bounds);
        }
      },
      renderer: {
        render: ({ count, position }) => {
          const marker = new google.maps.marker.AdvancedMarkerElement({
            position,
            zIndex: 1000 + count,
          });
          marker.appendChild(createMarkerContent(count, true));
          return marker;
        },
      },
    });

    setMarkerClusterer(prevCluster => {
      prevCluster?.clearMarkers();
      prevCluster?.setMap(null);
      return clusterer;
    });
    setSelectedPractitioners(null);
    setClusterPractitioners(null);
    setFocusedPractitioner?.(null);
  }, [ geocodedPractitioners, isLoaded, createMarkerContent, setFocusedPractitioner]);

  const circleOptions = useMemo(() => ({
    strokeColor: '#0d9488',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: '#0d9488',
    fillOpacity: 0.1,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    zIndex: 1,
  }), []);

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full bg-red-50">
        <div className="text-center">
          <MapPinIcon className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-red-600 font-medium">Failed to load Google Maps</p>
          <p className="text-red-500 text-sm">Please check your API key configuration</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return <Loading message="Loading map..." />;
  }

  return (
    <div className="relative h-full">
      {isGeocoding && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-600">Loading locations...</span>
        </div>
      )}

      <GoogleMap
        mapContainerStyle={{
          width: '100%',
          height: '100%',
        }}
        center={zipcodeCenter || { lat: 39.8283, lng: -98.5795 }}
        zoom={MAX_ZOOM}
        onLoad={(map: google.maps.Map) => {
          mapRef.current = map;
          geocoderRef.current = new google.maps.Geocoder();
          calculateCircleCenter();
        }}
        options={mapOptions}
      >
        {zipcodeCenter && (
          <CircleF
            center={zipcodeCenter}
            radius={milesToMeters(searchRadiusMiles)}
            options={circleOptions}
          />
        )}

        {selectedPractitioners && selectedPractitioners.length > 0 && (
          <InfoWindowF
            position={{ lat: selectedPractitioners[0].lat, lng: selectedPractitioners[0].lng }}
            onCloseClick={() => {
              setSelectedPractitioners(null);
              setClusterPractitioners(null);
              setFocusedPractitioner?.(null);
            }}
            options={{
              maxWidth: 320,
            }}
          >
            {selectedPractitioners.length > 1 ? (
              <div className="min-w-[280px] max-h-[300px] overflow-y-auto">
                <h3 className="font-bold text-gray-900 text-sm mb-2 sticky top-0 bg-white p-3 border-b z-10">
                  {selectedPractitioners.length} Healthcare Providers
                </h3>
                {selectedPractitioners.map((p) => (
                  <div
                    key={p.practitioner_role_id}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-0 flex items-center gap-3 transition-colors"
                    onClick={() => {
                      setClusterPractitioners(selectedPractitioners);
                      setSelectedPractitioners([p]);
                    }}
                  >
                    <div className="w-8 h-8 bg-brand-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <UserIcon className="w-4 h-4 text-brand-600" />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-gray-900">{p.name}</div>
                      <div className="text-xs text-brand-600 truncate max-w-[180px]">
                        {p.specialty[0]} {p.specialty.length > 1 ? 'and ' + (p.specialty.length - 1) + ' other' + (p.specialty.length - 1 > 1 ? 's' : '') : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="min-w-[280px]">
                {clusterPractitioners && clusterPractitioners.length > 1 && (
                  <button
                    onClick={() => {
                      setSelectedPractitioners(clusterPractitioners);
                      setClusterPractitioners(null);
                      setFocusedPractitioner?.(null);
                    }}
                    className="flex items-center gap-1.5 text-xs text-black hover:text-brand-600 mb-2 transition-colors"
                  >
                    <ArrowLeftIcon className="w-3.5 h-3.5" />
                    <span>Back to all {clusterPractitioners.length} providers</span>
                  </button>
                )}
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100">
                  <div className="w-10 h-10 bg-gradient-to-br from-brand-100 to-brand-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserIcon className="w-5 h-5 text-brand-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 text-base truncate">
                      {selectedPractitioners[0].name}
                    </h3>
                    <p className="text-brand-600 text-xs truncate">
                      {selectedPractitioners[0].specialty[0]} {selectedPractitioners[0].specialty.length > 1 ? 'and ' + (selectedPractitioners[0].specialty.length - 1) + ' other' + (selectedPractitioners[0].specialty.length - 1 > 1 ? 's' : '') : ''}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-start gap-2">
                    <BuildingOfficeIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-xs leading-relaxed">
                      {selectedPractitioners[0].location}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <a
                      href={`tel:${selectedPractitioners[0].locationPhone}`}
                      className="text-brand-600 hover:text-brand-700 text-xs font-medium"
                    >
                      {selectedPractitioners[0].locationPhone}
                    </a>
                  </div>

                  {selectedPractitioners[0].practitionerEmail && (
                    <div className="flex items-center gap-2">
                    <EnvelopeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600 text-xs truncate">
                        {selectedPractitioners[0].practitionerEmail}
                      </span>
                    </div>
                  )}
                </div>

                <Button
                  text="Schedule Appointment"
                  onClick={() => {
                    const practitioner = selectedPractitioners[0];
                    setSelectedPractitioners(null);
                    onPractitionerSelect(practitioner);
                  }}
                  className="w-full justify-center bg-brand-600 hover:bg-brand-700 py-2 text-xs"
                />
              </div>
            )}
          </InfoWindowF>
        )}
      </GoogleMap>

      <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-lg shadow-lg">
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-brand-600 bg-brand-600/10" />
            <span className="text-gray-600">{searchRadiusMiles} mile radius</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-brand-600 border-2 border-white shadow-sm" />
            <span className="text-gray-600">Healthcare provider</span>
          </div>
        </div>
      </div>

      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm p-1 rounded-lg shadow-lg space-y-2">
        <Button
          text="Recenter Circle"
          onClick={() => {
            calculateCircleCenter();
          }}
          icon={<MapPinIcon className="w-3.5 h-3.5 text-white" />}
          className="w-full justify-center bg-brand-600 hover:bg-brand-700 py-2 text-xs"
        />
      </div>
    </div>
  );
};

export default PractitionerMap;
